import { useCallback, useState } from "react";

import { useKeystone, useSelectedPolicy } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { type TingContextState } from "pages/offer/ting/ting-flow";

export type TingEnrollmentValues = {
  offeringId?: string;
  offerId?: string;
  insightPolicyId?: string;
  additionalDetails?: {
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    phone?: string;
    isMobilePhone?: boolean;
    consentSms?: boolean;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
  };
};

export const useTingEnrollment = (state: TingContextState) => {
  const keystone = useKeystone();
  const { data } = useSelectedPolicy();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const {
    firstName,
    lastName,
    emailAddress,
    phone,
    mobile,
    offeringId,
    offerId,
  } = state ?? {};

  const enrollTing = useCallback(async () => {
    setLoading(true);
    setError(false);
    const tingEnrollmentBody: TingEnrollmentValues = {
      offeringId,
      offerId,
      insightPolicyId: data?.keystonePolicyId,
      additionalDetails: {
        firstName,
        lastName,
        emailAddress,
        phone,
        isMobilePhone: mobile?.includes("isMobile"),
        consentSms: mobile?.includes("isTextOk"),
        addressLine1: data?.propertyAddress?.street1,
        addressLine2: data?.propertyAddress?.street2,
        city: data?.propertyAddress?.city,
        state: data?.propertyAddress?.state,
        postalCode: data?.propertyAddress?.zip,
      },
    };

    const response = await keystone.enrollTing(tingEnrollmentBody);
    if (isError(response)) {
      setError(true);
    }
    setLoading(false);

    return !isError(response);
  }, [
    emailAddress,
    firstName,
    keystone,
    lastName,
    mobile,
    offerId,
    offeringId,
    phone,
    data?.keystonePolicyId,
    data?.propertyAddress?.city,
    data?.propertyAddress?.state,
    data?.propertyAddress?.street1,
    data?.propertyAddress?.street2,
    data?.propertyAddress?.zip,
  ]);

  return { enrollTing, loading, error };
};
